/**
 * Drawing the realm's chronicle, and answering it — for the player and for the
 * twenty-nine crowns who are not.
 *
 * ── Where it sits in the month ───────────────────────────────────────────
 *
 * At the very end of `TurnPhase.Council`, after `refreshWarnings` and after the
 * turn counter has moved. That placement is the whole of the safety argument:
 * by the time a question is put, the month is *completely resolved* — marches
 * landed, battles fought, sieges counted, tithes collected, orders given,
 * warnings rebuilt. A pending question therefore suspends the **next** month
 * rather than freezing the realm halfway through this one, and there is no
 * half-finished state for a save to round-trip.
 *
 * It is on the council rather than in the tick, per the house rule. The cost is
 * one weighted draw for five of the thirty crowns per month and a walk of a
 * thirteen-card deck for each of them — see the sweep figure in COORDINATION.md.
 *
 * ── The suspension ───────────────────────────────────────────────────────
 *
 * `c.pendingEvent` is state in the full sense — hashed, saved, round-tripped —
 * and it stops `stepRealm` exactly as `c.pending` (a battle) does. That is
 * deliberate and it is the same argument the settlement's `pendingEvent` makes
 * in `step.ts`: a question the player is looking at must not be a *UI* pause,
 * because a UI pause is a fact about how long somebody stared at a screen, and
 * two replays that stared for different lengths of time would diverge.
 *
 * The realm freezing does **not** freeze the player's town. They are two
 * `World`s stepped together by `stepGame`, and only the strategic one is holding
 * its breath — which is correct: the question is being put to the crown, and the
 * masons should not down tools over it.
 *
 * ── Every crown answers ──────────────────────────────────────────────────
 *
 * The player's draw becomes `c.pendingEvent`. Every other crown's is answered on
 * the spot by `answerAsCrown`, out of the `ai` weight on each choice plus its
 * temperament. Nothing in `ai.ts` changes — not one line — because the knowledge
 * of what hanging the envoy *does* belongs on the card that says so, and putting
 * it there is what stopped this becoming a second policy engine that could drift
 * from the first.
 */

import { chance, nextFloat, pickWeighted, type Rng } from '../../core/rng.ts';
import type { World } from '../world.ts';
import { eligibleRealmEvents, realmEvent, realmEventContextValid } from './chronicle.ts';
import { logLine, nationName } from './state.ts';
import {
  EVENT_CHANCE,
  EVENT_FIRST_TURN,
  EVENT_PERIOD,
  MAX_EVENT_HISTORY,
  charge,
  costText,
  historyKey,
  payable,
  subjectHistoryKey,
  timesFiredBy,
  type PendingRealmEvent,
  type RealmChoice,
  type RealmCost,
  type RealmEventCtx,
  type RealmEventDef,
} from './chronicletypes.ts';
import type { CampaignState } from './types.ts';

/**
 * A month of the chronicle. Called once, last, from the council.
 *
 * Crowns are walked in index order and each is considered on its own six-month
 * stagger, so five of the thirty draw in any given month and the work is spread
 * across the year rather than landing in one lump. The loop deliberately does
 * **not** stop when the player's question is raised: stopping would mean every
 * crown with an index above the player's never got a draw on a month the player
 * did, which for a player at index 0 is *every crown, on every month it
 * mattered*. That is precisely the class of bug this project has shipped twice.
 */
export function realmEventTurn(w: World): void {
  const c = w.campaign;
  if (!c || c.over) return;
  if (c.turn < EVENT_FIRST_TURN) return;

  for (let n = 0; n < c.nations.length; n++) {
    if (!c.nations[n].alive) continue;
    if ((c.turn + n) % EVENT_PERIOD !== 0) continue;
    // The shared question slot belongs to its first human recipient. Other
    // human crowns wait for another council; AI crowns still answer this one.
    if (c.pendingEvent && c.humanNations.includes(n)) continue;

    /**
     * The roll is taken **before** the pool is consulted, and the order is
     * load-bearing rather than stylistic.
     *
     * Taken after, the number of draws off `rngCampaign` in a month would depend
     * on how many cards happened to be eligible — so adding a card to the deck,
     * or a crown taking a province, would shift every subsequent random number
     * in the campaign. The whole downstream realm would replay differently for a
     * reason that has nothing to do with anything anybody did. `director.ts`
     * makes the same move for the same reason.
     */
    const drew = chance(w.rngCampaign, EVENT_CHANCE);
    const pool = eligibleRealmEvents(c, n, (id) => timesFiredBy(c, n, id));
    if (!drew || pool.length === 0) continue;

    const picked = pickWeighted(
      w.rngCampaign,
      pool,
      pool.map((p) => Math.max(0.01, p.def.weight)),
    );

    /**
     * A question is put to a *person*, and in a room there are several.
     *
     * This asked `n === c.playerNation`, which names the crown whose screen
     * this is — the one field a room deliberately answers differently on every
     * machine, and meaningless on a server that plays nobody. `humanNations` is
     * the shared answer to the same question, and in a solo campaign it holds
     * exactly the player's own crown, so nothing changes there. The realm is
     * frozen for everybody while the question stands; whose it is, and who may
     * answer, is `pendingEvent.ctx.nation`.
     *
     * One slot rather than one per crown, deliberately: a card is answered out
     * of a treasury and changes a board everybody is standing on, so two
     * unanswered questions at once would be two crowns deciding against a realm
     * that had already moved under them.
     */
    if (c.humanNations.includes(n)) open(c, picked.def, picked.k);
    else answerAsCrown(c, w.rngCampaign, picked.def, picked.k);
  }
}

/**
 * Put the question to the player.
 *
 * The offered choice ids are frozen here, exactly as the settlement freezes
 * them: the offer must not be recomputed when the answer comes back, because the
 * answer has to mean what the player read — and it may come back several minutes
 * and one save file later.
 */
function open(c: CampaignState, def: RealmEventDef, k: RealmEventCtx): void {
  c.pendingEvent = {
    id: def.id,
    choices: def.choices.map((ch) => ch.id),
    ctx: { ...k },
    turn: c.turn,
  };
  logLine(c, `${def.title}. ${def.summary}`);
}

/**
 * Put a named card to a crown now, if the board will currently produce it.
 *
 * The door the tests use to walk every card and every branch, and the shape a
 * debug console verb would want. It refuses rather than inventing a context: a
 * card whose `when` says no is a card the board is not producing, and forcing one
 * anyway would let a test pass on a situation the game cannot reach — which is
 * the exact failure this deck's header is about.
 */
export function forceRealmEvent(c: CampaignState, id: string, nation = c.playerNation): boolean {
  if (c.pendingEvent) return false;
  const def = realmEvent(id);
  if (!def) return false;
  const k = def.when(c, nation);
  if (!k || !realmEventContextValid(c, def, k)) return false;
  open(c, def, k);
  return true;
}

/**
 * What a crown that is not the player does about it.
 *
 * Highest `ai` weight among the choices it can actually pay for, with a small
 * deterministic wobble so that thirty crowns cut from six temperaments do not
 * answer identically for two hundred months. Unpayable choices are skipped
 * rather than half-taken; the free-choice invariant guarantees at least one
 * survives that filter, so a crown can never be stuck.
 */
function answerAsCrown(c: CampaignState, rng: Rng, def: RealmEventDef, k: RealmEventCtx, offered?: string[]): void {
  let best: RealmChoice | undefined;
  let bestScore = -Infinity;
  let bestCost: RealmCost | undefined;
  for (const ch of def.choices) {
    if (offered && !offered.includes(ch.id)) continue;
    const cost = ch.cost?.(c, k);
    if (!payable(c, k.nation, cost)) continue;
    const score = ch.ai(c, k) + nextFloat(rng) * 0.25;
    if (score > bestScore) {
      bestScore = score;
      best = ch;
      bestCost = cost;
    }
  }
  if (!best) return;

  charge(c, k.nation, bestCost);
  record(c, k, def.id, best.id);
  const line = best.apply(c, k, rng);

  /**
   * Only the player's chronicle reaches the player's chronicle.
   *
   * Thirty crowns answering a question every eleven months is nearly three
   * events a month board-wide, and `MAX_LOG` is forty-eight lines. Logging all
   * of them would flush every march, siege and peace out of the log inside a
   * fortnight and leave the player reading somebody else's diary. So a crown's
   * answer is logged only when the player is the *subject* of it — the rival
   * whose envoy was sent back on a mule has a right to know.
   */
  if (k.other === c.playerNation) {
    logLine(c, `${nationName(c, k.nation)}: ${line}`);
  }
}

/**
 * Answer the player's question the way a crown would.
 *
 * The harness door, and it exists so that an idle seat in a balance sweep plays
 * *exactly* like the twenty-nine crowns around it rather than like somebody who
 * always clicks the first button. A harness whose player answered differently
 * from every AI would be measuring a realm nobody is playing — which is the
 * whole complaint `campaign-sweep.ts` was written to answer.
 */
export function answerAsCouncil(c: CampaignState, rng: Rng): boolean {
  const p = c.pendingEvent;
  if (!p) return false;
  const def = realmEvent(p.id);
  if (!def) {
    c.pendingEvent = null;
    return false;
  }
  if (!realmEventContextValid(c, def, p.ctx)) return dismissStale(c, def);
  c.pendingEvent = null;
  answerAsCrown(c, rng, def, p.ctx, p.choices);
  return true;
}

/** One answer into the record, oldest dropped first. */
function record(c: CampaignState, k: RealmEventCtx, eventId: string, choiceId: string): void {
  if (eventId === 'envoy_at_the_gate' || eventId === 'war_declared') {
    const turn = eventId === 'war_declared'
      ? c.diplo.wars.find(war => war.attacker === k.other && war.defender === k.nation)?.startTurn ?? c.turn
      : c.turn;
    c.eventHistory.push(subjectHistoryKey(k.nation, eventId, choiceId, k.other, turn));
  }
  c.eventHistory.push(historyKey(k.nation, eventId, choiceId));
  if (c.eventHistory.length > MAX_EVENT_HISTORY) {
    let drop = c.eventHistory.length - MAX_EVENT_HISTORY;
    // A subject marker and its legacy answer are one memory. Keeping only the
    // answer would make a recent, identified insult look like an old save's
    // anonymous insult and allow its follow-up to choose an unrelated rival.
    const lastRemoved = c.eventHistory[drop - 1];
    if (lastRemoved.startsWith('@') && c.eventHistory[drop] === lastRemoved.slice(1).split('|')[0]) drop++;
    c.eventHistory.splice(0, drop);
  }
}

function dismissStale(c: CampaignState, def: RealmEventDef): boolean {
  c.pendingEvent = null;
  logLine(c, `${def.title}: circumstances have changed. The matter is dismissed without cost.`);
  return true;
}

/**
 * The player's answer. Returns false when nothing happened, which the command
 * door treats as an ordinary event rather than an error.
 *
 * Five properties are copied verbatim from `doResolveEvent` in `apply.ts`,
 * because every one of them is load-bearing:
 *
 *   1. Naming the event as well as the index is a **staleness guard** — an
 *      answer from a modal that has already been dismissed cannot answer the one
 *      currently on the table with whatever index it happened to carry. An empty
 *      id is permissive, which is what lets a test or a bot send a bare answer.
 *   2. The index must be an offered integer. A malformed or stale index spends
 *      nothing and leaves the question on the table.
 *   3. It resolves through the *frozen offered list* to a choice **id**, never
 *      through deck order.
 *   4. An unaffordable choice is **refused without unfreezing** — the question
 *      stays on the table and the player picks again. Every card carries a free
 *      choice, so this cannot deadlock.
 *   5. A card that has vanished from the deck under a save **drops the question**
 *      rather than freezing the realm forever.
 */
export function answerRealmEvent(
  c: CampaignState,
  rng: Rng,
  eventId: string,
  index: number,
): boolean {
  const p = c.pendingEvent;
  if (!p) return false;
  if (eventId && eventId !== p.id) return false;
  if (!Number.isInteger(index) || index < 0 || index >= p.choices.length) return false;

  const def = realmEvent(p.id);
  if (!def) {
    c.pendingEvent = null;
    return false;
  }
  if (!realmEventContextValid(c, def, p.ctx)) return dismissStale(c, def);

  const choice = def.choices.find((ch) => ch.id === p.choices[index]);
  if (!choice) {
    c.pendingEvent = null;
    return false;
  }

  const k = p.ctx;
  const cost = choice.cost?.(c, k);
  if (!payable(c, k.nation, cost)) return false;

  c.pendingEvent = null;
  record(c, k, def.id, choice.id);
  charge(c, k.nation, cost);
  logLine(c, choice.apply(c, k, rng));
  return true;
}

// ── What the world map has to draw ─────────────────────────────────────────

export interface RealmChoiceView {
  /** Index into the *offered* list. This is what `Cmd.CampaignAnswerEvent.a` carries. */
  index: number;
  id: string;
  label: string;
  /** The exact mechanics, already resolved against the frozen context. */
  rules: string;
  /** The price as a sentence, or '' when it is free. */
  price: string;
  /** False when the crown cannot pay. Draw it greyed and give it no handler. */
  affordable: boolean;
}

export interface RealmEventView {
  id: string;
  title: string;
  kind: string;
  summary: string;
  /** The paragraph, with the reason it fired already written into it. */
  flavour: string;
  /** Turn it was drawn. `${id}#${turn}` is the modal's identity. */
  turn: number;
  choices: RealmChoiceView[];
}

/**
 * Everything the modal needs, resolved once, off state alone.
 *
 * The UI never touches `REALM_EVENTS`, never calls a `rules` function and never
 * re-derives a context — exactly as the world map never touches campaign state
 * directly. It reads this and it sends one command back.
 */
export function realmEventView(c: CampaignState): RealmEventView | null {
  const p = c.pendingEvent;
  if (!p) return null;
  const def = realmEvent(p.id);
  if (!def) return null;
  const k = p.ctx;
  if (!realmEventContextValid(c, def, k)) return {
    id: def.id, title: def.title, kind: def.kind, summary: 'Circumstances have changed.', turn: p.turn,
    flavour: 'The original holding, host or rival is no longer in the situation described. This matter can be dismissed without spending anything or applying its former effects.',
    choices: [{ index: 0, id: 'dismiss_stale', label: 'Dismiss the matter.', rules: 'No resources are spent and no event effects are applied.', price: '', affordable: true }],
  };

  const choices: RealmChoiceView[] = [];
  p.choices.forEach((id, index) => {
    const ch = def.choices.find((x) => x.id === id);
    if (!ch) return;
    const cost = ch.cost?.(c, k);
    choices.push({
      index,
      id: ch.id,
      label: ch.label,
      rules: ch.rules(c, k),
      price: costText(cost),
      affordable: payable(c, k.nation, cost),
    });
  });

  return {
    id: def.id,
    title: def.title,
    kind: def.kind,
    summary: def.summary,
    flavour: def.flavour(c, k),
    turn: p.turn,
    choices,
  };
}

/** For a save that has to round-trip a question. */
export function loadPendingEvent(
  raw: PendingRealmEvent | null | undefined,
): PendingRealmEvent | null {
  if (!raw || typeof raw.id !== 'string') return null;
  const def = realmEvent(raw.id);
  if (!def || !Array.isArray(raw.choices)) return null;
  const choices = raw.choices.filter((id, index, all) => typeof id === 'string' && all.indexOf(id) === index && def.choices.some(ch => ch.id === id));
  // A removed card/choice must never leave an invisible pause or strand a poor
  // crown with only priced answers after a content update.
  if (!choices.some(id => def.choices.some(ch => ch.id === id && ch.cost === undefined))) return null;
  const k = raw.ctx;
  if (!k || !Number.isInteger(k.nation) || k.nation < 0) return null;
  const subjectIndex = (value: number | undefined) => typeof value === 'number' && Number.isInteger(value) && value >= -1 ? value : -1;
  return {
    id: raw.id,
    choices,
    ctx: {
      nation: k.nation,
      province: subjectIndex(k.province),
      other: subjectIndex(k.other),
      army: subjectIndex(k.army),
      region: subjectIndex(k.region),
    },
    turn: Number.isInteger(raw.turn) && raw.turn >= 0 ? raw.turn : 1,
  };
}
